/**
 * 日落橙模版 - 列表页
 * 
 * 设计理念：顶部筛选标签栏 + 瀑布流网格 + 加载更多
 * 与经典橙色（全宽竖排横条列表）完全不同：
 * - 顶部：分类标签栏（横向滚动药丸形）
 * - 排序：下拉选择器，内嵌在标签栏右侧
 * - 列表：Pinterest式瀑布流 2-3列（卡片高度多样化）
 * - 分页：加载更多按钮（非传统页码）
 * - 无侧边栏，全宽布局
 */

import { useEffect, useState, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/templates/sunset/components/Navbar";
import SunsetFooter from "@/templates/sunset/components/Footer";
import {
  getSiteCategories,
  getListings,
  getNavCategories,
  type FrontendCategory,
  type ListingItem,
} from "@/utils/api";
import { useSite } from "@/context/SiteContext";

const SORT_OPTIONS = [
  { key: "latest", label: "最新发布" },
  { key: "popular", label: "最多浏览" },
  { key: "alpha", label: "名称排序" },
] as const;

export default function SunsetCategoryPage() {
  const { slug } = useParams<{ slug?: string }>();
  const { siteId, loading: siteLoading } = useSite();

  const [categories, setCategories] = useState<FrontendCategory[]>([]);
  const [navCategories, setNavCategories] = useState<Array<{ id: string; name: string; slug: string; icon: string }>>([]);
  const [listings, setListings] = useState<ListingItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const [activeCategoryId, setActiveCategoryId] = useState<string>("");
  const [sortBy, setSortBy] = useState("latest");
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 12;

  useEffect(() => {
    if (siteLoading || !siteId) return;
    Promise.all([
      getSiteCategories().then(setCategories).catch(() => {}),
      getNavCategories().then(setNavCategories).catch(() => {}),
    ]);
  }, [siteId, siteLoading]);

  useEffect(() => {
    if (slug && categories.length > 0) {
      const found = categories.find((c) => c.slug === slug || c.id === slug);
      if (found) setActiveCategoryId(found.id);
    }
  }, [slug, categories]);

  // 加载数据（切换分类/排序时重置）
  useEffect(() => {
    if (siteLoading || !siteId) return;
    setLoading(true);
    setCurrentPage(1);
    const params: Record<string, any> = { page: 1, page_size: PAGE_SIZE };
    if (activeCategoryId) params.category_id = activeCategoryId;
    getListings(params)
      .then((data) => {
        setListings(data.list || []);
        setTotal(data.total || 0);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [siteId, siteLoading, activeCategoryId, sortBy]);

  // 加载更多
  const handleLoadMore = async () => {
    if (loadingMore || listings.length >= total) return;
    setLoadingMore(true);
    const nextPage = currentPage + 1;
    const params: Record<string, any> = { page: nextPage, page_size: PAGE_SIZE };
    if (activeCategoryId) params.category_id = activeCategoryId;
    try {
      const data = await getListings(params);
      setListings(prev => [...prev, ...(data.list || [])]);
      setCurrentPage(nextPage);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingMore(false);
    }
  };

  const currentCategoryName = useMemo(() => {
    if (activeCategoryId) {
      const cat = categories.find((c) => c.id === activeCategoryId);
      return cat?.name || "全部分类";
    }
    return "全部分类";
  }, [activeCategoryId, categories]);

  const sortedListings = useMemo(() => {
    const list = [...listings];
    if (sortBy === "popular") list.sort((a, b) => (b.view_count || 0) - (a.view_count || 0));
    if (sortBy === "alpha") list.sort((a, b) => a.title.localeCompare(b.title, "zh"));
    return list;
  }, [listings, sortBy]);

  const displayCategories = navCategories.length > 0 ? navCategories : categories;
  const hasMore = listings.length < total;

  return (
    <div className="min-h-screen bg-[#fffbf5]">
      <Navbar />

      {/* ======== 面包屑 ======== */}
      <div className="pt-20 pb-2">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <nav className="flex items-center gap-1.5 text-sm py-3">
            <Link to="/" className="text-gray-400 hover:text-amber-600 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line text-xs text-gray-300" />
            <span className="text-amber-600 font-semibold">{currentCategoryName}</span>
          </nav>
        </div>
      </div>

      {/* ======== 筛选栏：标签 + 排序 ======== */}
      <div className="sticky top-[68px] z-30 bg-[#fffbf5] pb-4">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-3">
            <div className="flex items-center gap-3">
              {/* 分类标签 - 横向滚动 */}
              <div className="flex items-center gap-1.5 overflow-x-auto flex-1 scrollbar-hide">
                <button
                  onClick={() => setActiveCategoryId("")}
                  className={`shrink-0 px-4 py-2 rounded-xl text-sm font-medium transition-all ${!activeCategoryId
                    ? "bg-amber-500 text-white shadow-sm shadow-amber-500/20"
                    : "bg-gray-50 text-gray-600 hover:bg-amber-50 hover:text-amber-600"
                  }`}
                >
                  全部
                </button>
                {displayCategories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setActiveCategoryId(cat.id === activeCategoryId ? "" : cat.id)}
                    className={`shrink-0 px-4 py-2 rounded-xl text-sm font-medium transition-all flex items-center gap-1.5 ${activeCategoryId === cat.id
                      ? "bg-amber-500 text-white shadow-sm shadow-amber-500/20"
                      : "bg-gray-50 text-gray-600 hover:bg-amber-50 hover:text-amber-600"
                    }`}
                  >
                    {cat.icon && <i className={`${cat.icon} text-xs`} />}
                    {cat.name}
                  </button>
                ))}
              </div>

              {/* 排序下拉 */}
              <div className="relative shrink-0">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="appearance-none pl-4 pr-9 py-2 bg-gray-50 rounded-xl text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-amber-400 cursor-pointer"
                >
                  {SORT_OPTIONS.map((opt) => (
                    <option key={opt.key} value={opt.key}>{opt.label}</option>
                  ))}
                </select>
                <i className="ri-arrow-down-s-line absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              </div>
            </div>
          </div>

          {/* 结果计数 */}
          <div className="mt-3 text-sm text-gray-400">
            共 <span className="font-semibold text-gray-700">{total}</span> 条信息
          </div>
        </div>
      </div>

      {/* ======== 瀑布流列表 ======== */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-8">
        {loading ? (
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-4">
            {[...Array(9)].map((_, i) => (
              <div key={i} className="break-inside-avoid animate-pulse bg-white rounded-2xl border border-gray-100 overflow-hidden mb-4">
                <div className="aspect-square bg-gray-200" />
                <div className="p-4 space-y-2">
                  <div className="w-3/4 h-4 bg-gray-200 rounded" />
                  <div className="w-full h-3 bg-gray-100 rounded" />
                  <div className="w-1/2 h-3 bg-gray-100 rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : sortedListings.length > 0 ? (
          <>
            <div className="columns-1 sm:columns-2 lg:columns-3 gap-4">
              {sortedListings.map((listing) => (
                <Link
                  key={listing.id}
                  to={`/listing/${listing.id}`}
                  className="break-inside-avoid block mb-4 group"
                >
                  <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-lg hover:border-amber-200 transition-all duration-300">
                    {/* 正方形缩略图 */}
                    <div className="aspect-square overflow-hidden relative">
                      {listing.images?.[0] ? (
                        <img
                          src={listing.images[0]}
                          alt={listing.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-400"
                        />
                      ) : (
                        <div className="w-full h-full bg-amber-50/50 flex flex-col items-center justify-center gap-1">
                          <i className="ri-image-line text-amber-300 text-3xl" />
                          <span className="text-[10px] text-amber-300">暂无图片</span>
                        </div>
                      )}
                      {listing.is_pinned && (
                        <span className="absolute top-3 left-3 text-[10px] px-2 py-0.5 rounded-full bg-amber-500 text-white font-bold shadow-sm">
                          顶
                        </span>
                      )}
                    </div>
                    <div className="p-4">
                      <h3 className="text-sm font-semibold text-gray-800 line-clamp-2 leading-snug group-hover:text-amber-600 transition-colors">
                        {listing.title}
                      </h3>
                      <p className="text-xs text-gray-400 mt-2 line-clamp-2 leading-relaxed">
                        {listing.content?.substring(0, 80) || "暂无描述"}
                      </p>
                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-50">
                        <span className="text-[11px] px-2 py-0.5 bg-amber-50 text-amber-600 rounded-full font-medium">
                          {listing.category?.name || "未分类"}
                        </span>
                        <span className="text-[11px] text-gray-400 flex items-center gap-1">
                          {sortBy === "popular" ? (
                            <><i className="ri-eye-line" />{listing.view_count}</>
                          ) : (
                            <><i className="ri-time-line" />{formatDate(listing.created_at)}</>
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {/* 加载更多 */}
            {hasMore && (
              <div className="text-center mt-8">
                <button
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  className="inline-flex items-center gap-2 px-10 py-3.5 bg-white border-2 border-amber-200 text-amber-600 font-semibold rounded-2xl hover:bg-amber-50 hover:border-amber-300 transition-all disabled:opacity-50"
                >
                  {loadingMore ? (
                    <>
                      <div className="w-5 h-5 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
                      加载中...
                    </>
                  ) : (
                    <>
                      加载更多 <i className="ri-arrow-down-line" />
                    </>
                  )}
                </button>
                <p className="text-xs text-gray-400 mt-2">
                  已显示 {listings.length} / {total} 条
                </p>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-20 bg-white rounded-3xl border border-gray-100">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-amber-50">
              <i className="ri-inbox-line text-2xl text-amber-400" />
            </div>
            <p className="text-gray-500 font-medium">该分类暂无信息</p>
            <p className="text-gray-400 text-sm mt-1">换个分类试试，或返回首页</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 mt-5 px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-600 text-white text-sm font-bold rounded-2xl hover:from-amber-600 hover:to-orange-700 transition-all shadow-md"
            >
              <i className="ri-arrow-left-line" />返回首页
            </Link>
          </div>
        )}
      </div>

      <SunsetFooter />
    </div>
  );
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}分钟前`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}小时前`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}天前`;
  return `${d.getMonth() + 1}-${d.getDate()}`;
}
